import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusPill, Empty } from "./ui";
import { fmtDateTime, maskPhone } from "@/lib/format";
import { dddUF } from "@/lib/ddd";
import { DddSummary } from "./DddSummary";

const CatNames = ({ ids }) => {
  const { categories } = useOutletContext();
  const names = ids.map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean);
  return <span className="text-sm">{names.join(", ") || "—"}</span>;
};

export default function Participants() {
  const { api } = useOutletContext();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/participants", { params: { q: q || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([])), tick ? 0 : 250);
    return () => clearTimeout(t);
  }, [api, q, tick]);
  const shown = !rows || !pick ? rows : rows.filter((p) => (pick.startsWith("uf:") ? (dddUF(p.whatsapp.slice(0, 2)) || "?") === pick.slice(3) : p.whatsapp.startsWith(pick)));
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Participantes" description="Cadastros feitos na Central Digital.">
        <div className="relative w-72 max-w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="participants-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar" className="h-10 pl-9" />
        </div>
      </PageHeader>
      <DddSummary api={api} active={pick} onPick={setPick} tick={tick} />
      <div className="scroll-thin min-h-0 flex-1 overflow-auto bg-white">
        {shown === null ? <Empty>Carregando…</Empty> : shown.length === 0 ? <Empty testId="participants-empty">{pick || q ? "Ninguém encontrado com esse filtro." : "Nenhum participante cadastrado."}</Empty> : (
          <Table data-testid="participants-table">
            <TableHeader>
              <TableRow><TableHead>Nome</TableHead><TableHead>WhatsApp</TableHead><TableHead>Cidade</TableHead><TableHead>Estado</TableHead><TableHead>Última mensagem</TableHead><TableHead>Data e horário</TableHead><TableHead>Categoria</TableHead><TableHead>Situação</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((p) => (
                <TableRow key={p.id} data-testid={`participant-row-${p.id}`} className="cursor-pointer" onClick={() => nav(`/admin/conversas?p=${p.id}`)}>
                  <TableCell className="font-medium">{p.name}{p.deletion_requested_at && <span className="ml-2 text-xs font-semibold text-red-700">Pediu exclusão</span>}</TableCell>
                  <TableCell className="whitespace-nowrap">{maskPhone(p.whatsapp)}</TableCell>
                  <TableCell>{p.city}</TableCell>
                  <TableCell>{p.state}</TableCell>
                  <TableCell className="max-w-[260px] truncate text-muted-foreground">{p.last_message_preview || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDateTime(p.last_message_at)}</TableCell>
                  <TableCell><CatNames ids={p.last_category_ids || []} /></TableCell>
                  <TableCell>{p.last_message_at ? <StatusPill status={p.status} /> : <span className="text-xs text-muted-foreground">Sem envios</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
